import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  FormControl,
  Select,
  InputLabel,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Menu as MenuIcon,
  Logout,
  Settings,
  Language as LanguageIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Storefront,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth, useCurrentUser } from '../auth';
import { useApi } from '../api';
import {
  ORGANIZATION_LIST_PERMISSION,
  ORGANIZATION_IMPERSONATE_PERMISSION,
  BILLING_MANAGE_PERMISSION,
  BILLING_PURCHASE_PACK_PERMISSION,
  BILLING_PURCHASE_SUBSCRIPTION_PERMISSION,
} from '../constants/permissions';

interface TopBarProps {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
}

interface OrganizationOption {
  id: string;
  name: string;
}

const DRAWER_WIDTH = 236;

const usePageTitle = (): string => {
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname;

  if (path === '/dashboard') return t('nav.dashboard');
  if (path === '/products') return t('nav.products');
  if (path.startsWith('/products/')) return t('nav.products');
  if (path === '/printing') return t('printing.title');
  if (path === '/users') return t('organizationUsers.title');
  if (path === '/settings') return t('userSettings.title');
  if (path === '/organization/setup') return t('organizationSetup.title');
  if (path === '/billing/store') return t('nav.billingStore');
  if (path === '/billing/info' || path === '/billing/subscribe') return t('nav.billingStore');
  return t('appTitle');
};

const TopBar: React.FC<TopBarProps> = ({ onMenuToggle, sidebarOpen }) => {
  const { authenticated, user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const pageTitle = usePageTitle();
  const { permissions, currentUser, impersonatedOrgId, setImpersonatedOrgId } = useCurrentUser();
  const api = useApi();
  const apiRef = React.useRef(api);
  const organizationsLoadedRef = React.useRef(false);
  const NO_ORG_VALUE = '__no_org__';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canImpersonateOrganizations =
    permissions.includes(ORGANIZATION_LIST_PERMISSION) &&
    permissions.includes(ORGANIZATION_IMPERSONATE_PERMISSION);
  const canAccessBillingStore = 
    permissions.includes(BILLING_MANAGE_PERMISSION) ||
    permissions.includes(BILLING_PURCHASE_PACK_PERMISSION) ||
    permissions.includes(BILLING_PURCHASE_SUBSCRIPTION_PERMISSION);
  const showOrgSwitcher = Boolean(authenticated) && canImpersonateOrganizations;
  const [organizations, setOrganizations] = React.useState<OrganizationOption[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = React.useState(false);
  const currentOrgId = currentUser?.org_id ?? null;
  const showNoOrgOption = currentOrgId === null;

  React.useEffect(() => {
    apiRef.current = api;
  }, [api]);

  React.useEffect(() => {
    if (!showOrgSwitcher) {
      setOrganizations([]);
      setOrganizationsLoading(false);
      organizationsLoadedRef.current = false;
      if (impersonatedOrgId && impersonatedOrgId !== currentOrgId) {
        setImpersonatedOrgId(currentOrgId ?? null);
      }
      return;
    }
  }, [showOrgSwitcher, currentOrgId, impersonatedOrgId, setImpersonatedOrgId]);

  React.useEffect(() => {
    if (!showOrgSwitcher) {
      return;
    }

    if (organizationsLoadedRef.current) {
      return;
    }

    let active = true;
    setOrganizationsLoading(true);
    apiRef.current
      .get('/organizations')
      .then((data) => {
        if (!active) {
          return;
        }
        const list: OrganizationOption[] = Array.isArray(data) ? data : [];
        setOrganizations(list);
        organizationsLoadedRef.current = true;
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load organizations', error);
        }
      })
      .finally(() => {
        if (active) {
          setOrganizationsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [showOrgSwitcher]);

  React.useEffect(() => {
    if (!showOrgSwitcher || organizations.length === 0) {
      return;
    }

    if (impersonatedOrgId && organizations.some((org) => org.id === impersonatedOrgId)) {
      return;
    }

    const currentOrgAvailable = currentOrgId ? organizations.some((org) => org.id === currentOrgId) : false;

    if (impersonatedOrgId === null) {
      if (currentOrgAvailable) {
        setImpersonatedOrgId(currentOrgId);
      }
      return;
    }

    const fallback = organizations[0]?.id;
    if (fallback) {
      setImpersonatedOrgId(fallback);
    }
  }, [showOrgSwitcher, organizations, currentOrgId, impersonatedOrgId, setImpersonatedOrgId]);

  const selectedOrgId = (impersonatedOrgId ?? currentOrgId) ?? (showNoOrgOption ? NO_ORG_VALUE : '');

  const handleOrganizationChange = (event: SelectChangeEvent<string>) => {
    const nextOrgId = event.target.value;
    if (!nextOrgId || nextOrgId === NO_ORG_VALUE) {
      setImpersonatedOrgId(null);
      return;
    }
    setImpersonatedOrgId(nextOrgId);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const languageMenuOpen = Boolean(languageMenuAnchor);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    handleLanguageMenuClose();
    handleMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const userName = user?.profile?.name || user?.profile?.preferred_username || 'User';
  const userEmail = user?.profile?.email;
  const userInitials = userName
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const currentLanguage = i18n.language;
  const availableLanguages = [
    { code: 'en', name: t('language.english'), flag: '🇺🇸' },
    { code: 'pl', name: t('language.polish'), flag: '🇵🇱' }
  ];

  return (
    <AppBar
      position='fixed'
      elevation={0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        width: !isMobile && sidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
        marginLeft: !isMobile && sidebarOpen ? `${DRAWER_WIDTH}px` : 0,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      <Toolbar sx={{ minHeight: '56px !important', px: 2.5 }}>
        {/* Menu Button */}
        <IconButton
          color='inherit'
          aria-label='toggle sidebar'
          onClick={onMenuToggle}
          edge='start'
          sx={{
            mr: 1.5,
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Page Title - Dynamic based on route */}
        <Typography
          variant='h6'
          noWrap
          component='div'
          sx={{
            flexGrow: 1,
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
          }}
        >
          {pageTitle}
        </Typography>

        {/* Language selector for unauthenticated users */}
        {!authenticated && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('nav.language')}>
              <IconButton
                onClick={handleLanguageMenuClick}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <LanguageIcon />
              </IconButton>
            </Tooltip>
            
            {/* Language Menu for unauthenticated users */}
            <Menu
              anchorEl={languageMenuAnchor}
              open={languageMenuOpen}
              onClose={handleLanguageMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              sx={{
                mt: 1.5,
                '& .MuiPaper-root': {
                  minWidth: 180,
                  boxShadow: theme.shadows[2],
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.25,
                },
              }}
            >
              {availableLanguages.map((language) => (
                <MenuItem
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  selected={currentLanguage === language.code}
                  sx={{
                    px: 2,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>
                      {language.flag}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={language.name} />
                  {currentLanguage === language.code && (
                    <CheckIcon sx={{ color: 'primary.main', ml: 1 }} fontSize="small" />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}

        {authenticated && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>

            {showOrgSwitcher && (
            <Box sx={{ minWidth: 210 }}>
                <FormControl size='small' fullWidth>
                  <InputLabel id='topbar-organization-select-label'>{t('nav.organization')}</InputLabel>
                  <Select
                    labelId='topbar-organization-select-label'
                    label={t('nav.organization')}
                    value={selectedOrgId}
                    onChange={handleOrganizationChange}
                    displayEmpty
                    disabled={organizationsLoading || organizations.length === 0}
                    renderValue={(value) => {
                      const val = value as string;
                      if (!val || val === NO_ORG_VALUE) {
                        if (showNoOrgOption) {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography component='span'>{t('nav.noOrganization')}</Typography>
                              <StarIcon fontSize='small' sx={{ color: 'warning.main' }} />
                            </Box>
                          );
                        }
                        return organizationsLoading ? t('nav.loading') : t('nav.selectOrganization');
                      }
                      const selected = organizations.find((org) => org.id === val);
                      if (!selected) {
                        return val;
                      }
                      const isHomeOrg = currentOrgId === selected.id;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography component='span'>{selected.name}</Typography>
                          {isHomeOrg && (
                            <StarIcon fontSize='small' sx={{ color: 'warning.main' }} />
                          )}
                        </Box>
                      );
                    }}
                    sx={{ backgroundColor: 'background.paper' }}
                  >
                    {showNoOrgOption && (
                      <MenuItem value={NO_ORG_VALUE}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Typography component='span'>{t('nav.noOrganization')}</Typography>
                          <StarIcon fontSize='small' sx={{ ml: 1, color: 'warning.main' }} />
                        </Box>
                      </MenuItem>
                    )}
                    {organizations.map((org) => {
                      const isHomeOrg = currentOrgId === org.id;
                      return (
                        <MenuItem key={org.id} value={org.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography component='span'>{org.name}</Typography>
                            {isHomeOrg && (
                              <StarIcon fontSize='small' sx={{ ml: 1, color: 'warning.main' }} />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {!isMobile && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                    {userName}
                  </Typography>
                  {userEmail && (
                    <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1 }}>
                      {userEmail}
                    </Typography>
                  )}
                </Box>
              )}
              
              <IconButton
                onClick={handleMenuClick}
                sx={{
                  p: 0,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'primary.main',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  {userInitials}
                </Avatar>
              </IconButton>
            </Box>

            {/* User Menu Dropdown */}
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              sx={{
                mt: 1.5,
                '& .MuiPaper-root': {
                  minWidth: 200,
                  boxShadow: theme.shadows[2],
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.25,
                },
              }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                  {userName}
                </Typography>
                {userEmail && (
                  <Typography variant='caption' color='text.secondary'>
                    {userEmail}
                  </Typography>
                )}
              </Box>
              <MenuItem 
                onClick={handleLanguageMenuClick}
                sx={{ 
                  px: 3, 
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>
                  <LanguageIcon sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText>{t('nav.language')}</ListItemText>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {availableLanguages.find(lang => lang.code === currentLanguage)?.flag}
                </Typography>
              </MenuItem>

              <MenuItem
                component={RouterLink}
                to='/settings'
                sx={{ 
                  px: 3, 
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Settings sx={{ mr: 2, color: 'text.secondary' }} />
                {t('nav.accountSettings')}
              </MenuItem>

              {canAccessBillingStore && (
                <MenuItem
                  component={RouterLink}
                  to='/billing/store'
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Storefront sx={{ mr: 2, color: 'text.secondary' }} />
                  {t('nav.billingStore')}
                </MenuItem>
              )}

              <Divider />
              
              <MenuItem 
                onClick={handleLogout}
                sx={{ 
                  px: 3, 
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>
                  <Logout sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText>{t('nav.logout')}</ListItemText>
              </MenuItem>
            </Menu>

            {/* Language Menu */}
            <Menu
              anchorEl={languageMenuAnchor}
              open={languageMenuOpen}
              onClose={handleLanguageMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
              sx={{
                '& .MuiPaper-root': {
                  minWidth: 180,
                  boxShadow: theme.shadows[2],
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.25,
                },
              }}
            >
              {availableLanguages.map((language) => (
                <MenuItem
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  selected={currentLanguage === language.code}
                  sx={{
                    px: 2,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>
                      {language.flag}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={language.name} />
                  {currentLanguage === language.code && (
                    <CheckIcon sx={{ color: 'primary.main', ml: 1 }} fontSize="small" />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;

